/**
 * Parses multi-language product descriptions from Syrve API
 * Format: <en>English text</en><ka>Georgian text</ka><ru>Russian text</ru>
 * 
 * @param {string} text - The multi-language text to parse
 * @param {string} locale - The current locale ('en', 'ge', 'ru', etc.)
 * @returns {string} - The text in the requested language or fallback
 */
export function parseMultiLanguageDescription(text, locale) {
  if (!text || typeof text !== 'string') {
    return '';
  }

  // Map app locale to Syrve language codes
  const localeMap = {
    'en': 'en',
    'ge': 'ka', // Georgian: app uses 'ge', Syrve uses 'ka'
    'ru': 'ru',
    'it': 'it', // Italian
    'tr': 'tr', // Turkish
    'ar': 'ar'  // Arabic
  };

  // Get target language code
  const targetLang = localeMap[locale] || 'en';

  // Try to extract the content for the target language using XML-style tags
  const regex = new RegExp(`<${targetLang}>(.*?)</${targetLang}>`, 's');
  const match = text.match(regex);

  if (match && match[1]) {
    return match[1].trim();
  }

  // Fallback order: try English first, then Georgian, then original
  if (targetLang !== 'en') {
    const enRegex = /<en>(.*?)<\/en>/s;
    const enMatch = text.match(enRegex);
    if (enMatch && enMatch[1]) {
      return enMatch[1].trim();
    }
  }

  if (targetLang !== 'ka') {
    const kaRegex = /<ka>(.*?)<\/ka>/s;
    const kaMatch = text.match(kaRegex);
    if (kaMatch && kaMatch[1]) {
      return kaMatch[1].trim();
    }
  }

  // If no language tags found, return original text
  return text;
}

/**
 * Checks if text contains multi-language formatting
 * @param {string} text - The text to check
 * @returns {boolean} - True if text contains language tags
 */
export function hasMultiLanguageFormat(text) {
  if (!text || typeof text !== 'string') {
    return false;
  }
  return /<(?:en|ka|ru)>.*?<\/(?:en|ka|ru)>/.test(text);
}

